import CallIcon from "@mui/icons-material/Call";
import { Box, CircularProgress, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { fetchCallsBlacklistList } from "../../../store/thunks/callsBlacklistThunk";
import { fetchCompanyList } from "../../../store/thunks/companyListThunks";
import { fetchNotificationListData } from "../../../store/thunks/notificationListThunk";
import { fetchPeopleListData } from "../../../store/thunks/peopleListThunks";
import { fetchPhoneListData } from "../../../store/thunks/phoneListThunk";
import { fetchVesselListData } from "../../../store/thunks/vesselThunk";
import {
  fetchJobsListWithBerthDataStatic,
  transformJobsData,
} from "../../../store/thunks/voyagesThunks";
import PhoneUtils from "../../../utils/PhoneUtils";

import CallCard from "./callCard";

import type { AppDispatch, RootState } from "../../../store/store";
import type { CompanyList } from "../../../utils/types/companyList";
import type { CallData } from "../../../utils/types/incomingCall";
import type { People } from "../../../utils/types/peopleList";
import type { Voyage } from "../../../utils/types/voyages";

interface ContactSearchResult {
  contactDetails: People | CompanyList | any | null;
  contactType: "people" | "company" | "vessel" | "blacklist" | "phone" | null;
}

interface VesselArrangements {
  deliveries: any[];
  bunkers: any[];
  onboardServices: any[];
}

const CallSidebar = () => {
  const { incomingCallsList } = useSelector(
    (state: RootState) => state.incomingCalls
  );
  const [filteredCallData, setFilteredCallData] = useState<CallData[]>([]);
  const [currentTime, setCurrentTime] = useState(dayjs());
  const { peopleList, loading: peopleLoading } = useSelector(
    (state: RootState) => state.peopleList
  );
  const { companyList, loading: companyLoading } = useSelector(
    (state: RootState) => state.companyList
  );
  const { vesselList } = useSelector((state: RootState) => state.vesselList);
  const { callsBlackList } = useSelector(
    (state: RootState) => state.callBlackList
  );
  const { phoneList } = useSelector((state: RootState) => state.phoneList);
  const { notificationList } = useSelector(
    (state: RootState) => state.notificationList
  );
  const {
    jobListStatic,
    staticJobListLoading: voyageLoading,
    newJobListStaticAvailable,
  } = useSelector((state: RootState) => state.voyages);

  const dispatch = useDispatch<AppDispatch>();

  // const sampleData: any = [
  //   {
  //     id: "1",
  //     name: "TMA_006",
  //     phone: "+91 1234567890",
  //     jobId: "JOB_001",
  //     person: {
  //       personName: "Jithesh",
  //       position: "Port Captain",
  //       title: "TMA Developer",
  //       company: "Tech Solutions",
  //     },
  //     persons: [
  //       {
  //         companyEmail : null,
  //         companyId : null,
  //         companyName : null,
  //         modifiedDt : "2025-08-12T01:16:33.864958",
  //         personEmail : "adriana.chapman@theviswagroup.com",
  //         personId : "c5edcb81-e48f-4eb4-b797-44e3aed21879",
  //         personName : "Adriana Chapman",
  //         recipientId : "c5edcb81-e48f-4eb4-b797-44e3aed21879",
  //         type: "PERSON"
  //       },
  //       {
  //         companyEmail : "LOrozco@wfscorp.com; BBARRETT@wfscorp.com",
  //         companyId : "ec7d8039-3bdc-4869-848e-f6b4fdaba621",
  //         companyName : "World Fuel Services",
  //         modifiedDt : "2022-12-31T20:43:50.179818",
  //         personEmail : null,
  //         personId : null,
  //         personName : null,
  //         recipientId : "ec7d8039-3bdc-4869-848e-f6b4fdaba621",
  //         type : "COMPANY",
  //       }
  //     ],
  //     vessel: {
  //       vesselName: "Vessel A",
  //       voyage: "123",
  //       operator: "Operator A",
  //       managementCompany: "Management A",
  //       currentStatus: "Docked",
  //       currentBerth: "Berth A",
  //       currentBerthId: "Berth A",
  //       nextBerth: "Berth B",
  //       nextBerthId: "Berth B",
  //     },
  //     arrangements: {
  //       deliveries: [
  //         {
  //           id: "1",
  //           berth: "Berth A",
  //           vendor: "Vendor A",
  //           deliveryType: "Fuel",
  //           palletCount: 10,
  //           deliveryDate: "2023-10-01",
  //           launch: "Launch A",
  //         },
  //         {
  //           id: "1",
  //           berth: "Berth B",
  //           vendor: "Vendor B",
  //           deliveryType: "Supplies",
  //           palletCount: 10,
  //           deliveryDate: "2023-10-01",
  //           launch: "Launch B",
  //         },
  //       ],
  //       bunkers: [
  //         {
  //           id: "1",
  //           berth: "Berth A",
  //           supplier: "Supplier A",
  //           mts: 100,
  //           oilType: "IFO",
  //           deliveryDate: "2023-10-01",
  //           type: "Bunker",
  //         },
  //       ],
  //       onboardServices: [
  //         {
  //           id: "1",
  //           berth: "Berth A",
  //           vendor: "Vendor A",
  //           typeOfService: "Fresh Water",
  //           dateCompleted: "2023-10-01",
  //         },
  //       ],
  //     },
  //     active: true,
  //     time: "18:22 (3 min ago)",
  //     bunkers:[{
  //       bfwId : "4b3f7276-cef4-4c4d-b46e-08d0ca617474",
  //       deliveryDate : "2025-09-13T05:05:00",
  //       dockLocation : "TPC - Port Neches",
  //       mts : 50,
  //       palletCount : null,
  //       supplier : "Peninsula",
  //       type : "test",
  //     },{
  //     bfwId: "6e6efd0d-dc8d-45b3-be00-974643102d40",
  //     deliveryDate: null,
  //     dockLocation: "TPC - Port Neches",
  //     mts: null,
  //     palletCount: null,
  //     supplier: null,
  //     type: null
  //     }],
  //     deliveries:[{
  //       deliveryDate :  null,
  //       deliveryType :  "Barge Slops disposal",
  //       deliveryVendorId :  "50b4b0fa-a080-446e-b52b-ac14da62bdb9",
  //       dockLocation :  "TPC - Port Neches",
  //       launch :  null,
  //       palletCount :  null,
  //       vendor :  "Circon",}
  //     ],
  //     onBoardServices:[{
  //       dateCompleted : "2025-09-13T05:05:00",
  //       dockLocation : "TPC - Port Neches",
  //       obfId : "d1f3f0e2-4b1c-4e7c-8f3a-4e5b8e6c9f1a",
  //       typeOfService : "Laundry",
  //       vendor : "Vendor A",
  //     },{
  //       dateCompleted :  null,
  //       dockLocation :  "TPC - Port Neches",
  //       obfId :  "a25b0914-041c-4f39-a184-29dffebbb15d",
  //       typeOfService :  null,
  //       vendor :  "Accord Intl SALES"}
  //     ],
  //   },
  //   {
  //     id: "1",
  //     time: "18:22 (3 min ago)",
  //     name: null,
  //     phone: "+91 1234567890",
  //   },
  //   {
  //     id: "1",
  //     time: "18:22 (3 min ago)",
  //     name: "TMA_006",
  //     phone: "+91 1234567890",
  //     company: {
  //       name: "ABL GROUP",
  //       type: "Surveyor",
  //     },
  //   },
  //   {
  //     id: "1",
  //     time: "18:22 (3 min ago)",
  //     name: "TMA_007",
  //     phone: "+91 1234567890",
  //     person: {
  //       personName: "Test User",
  //       personId: "12345",
  //       position: "Surveyor",
  //       title: "Surveyor",
  //       company: "Tech Solutions",
  //     },
  //     voyages: [
  //       {
  //         id: "V123",
  //         voyage: "V.123",
  //         vesselName: "Vessel A",
  //         vesselId: "V12345",
  //         status: "Docked",
  //         currentBerth: "Berth 1",
  //         nextBerth: "Berth 2",
  //         agent: "Agent A",
  //         management: "Management A",
  //         operator: "Operator A",
  //         cargo: [
  //           {
  //             cargoId: "59791e7f-9ce8-42c0-b0ed-9e92ae0d970c",
  //             lockFact: false,
  //             number: 0,
  //             operation: "Loading",
  //             grade: "CARBON TETRA CHLORIDE",
  //             charterer: "Charterer A",
  //             quantity: 10,
  //             option: "Option A",
  //             surveyor: "Surveyor A",
  //             orders: 2,
  //             berthId: "64dca1e1-adcb-4ac4-953d-d30014cf1f75",
  //             berth: "Berth 1",
  //             jobId: "JOB_001",
  //           },
  //           {
  //             cargoId: "59791e7f-9ce8-42c0-b0ed-9e92ae0d970r",
  //             lockFact: false,
  //             number: 0,
  //             operation: "Discharging",
  //             grade: "oil",
  //             charterer: "Charterer B",
  //             quantity: 10,
  //             option: "Option B",
  //             surveyor: "Surveyor B",
  //             orders: 2,
  //             berthId: "64dca1e1-adcb-4ac4-953d-d30014cf1f75",
  //             berth: "Berth 1",
  //             jobId: "JOB_001",

  //           },
  //         ],
  //       },
  //       {
  //         id: "V123",
  //         voyage: "v.126",
  //         vesselName: "Vessel B",
  //         vesselId: "V12345",
  //         status: "Docked",
  //         currentBerth: "Berth 1",
  //         nextBerth: "Berth 2",
  //         agent: "Agent A",
  //         management: "Management A",
  //         operator: "Operator A",
  //         cargo: [
  //           {
  //             cargoId: "59791e7f-9ce8-42c0-b0ed-9e92ae0d970c",
  //             lockFact: false,
  //             number: 0,
  //             operation: "Loading",
  //             grade: "CARBON TETRA CHLORIDE",
  //             charterer: "Charterer A",
  //             quantity: 10,
  //             option: "Option A",
  //             surveyor: "Surveyor A",
  //             orders: 2,
  //             berthId: "64dca1e1-adcb-4ac4-953d-d30014cf1f75",
  //             berth: "Berth 1",
  //           },
  //           {
  //             cargoId: "59791e7f-9ce8-42c0-b0ed-9e92ae0d970r",
  //             lockFact: false,
  //             number: 0,
  //             operation: "Discharging",
  //             grade: "oil",
  //             charterer: "Charterer B",
  //             quantity: 10,
  //             option: "Option B",
  //             surveyor: "Surveyor B",
  //             orders: 2,
  //             berthId: "64dca1e1-adcb-4ac4-953d-d30014cf1f75",
  //             berth: "Berth 1",
  //           },
  //         ],
  //       },
  //     ],
  //   },
  //   {
  //     id: "1",
  //     time: "18:22 (3 min ago)",
  //     name: "TMA_008",
  //     phone: "+91 1234567890",
  //     company: {
  //       name: "ABL GROUP",
  //       type: "Surveyor",
  //     },
  //   },
  // ];

  useEffect(() => {
    // Single interval to update current time and filter old calls
    const interval = setInterval(() => {
      const now = dayjs();
      setCurrentTime(now);
      setFilteredCallData((prevData) => filterOldCalls(prevData ?? []));
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!jobListStatic.length || newJobListStaticAvailable) {
      dispatch(
        fetchJobsListWithBerthDataStatic({
          page: 0,
          pageSize: 50,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newJobListStaticAvailable]);

  useEffect(() => {
    if (!peopleList.length) {
      dispatch(fetchPeopleListData());
    }
    if (!companyList.length) dispatch(fetchCompanyList());
    if (!vesselList.length) dispatch(fetchVesselListData());
    if (!callsBlackList.length) dispatch(fetchCallsBlacklistList());
    if (!phoneList.length) dispatch(fetchPhoneListData());
    if (!notificationList.length) dispatch(fetchNotificationListData());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const voyageList = useMemo(
    () => transformJobsData(jobListStatic),
    [jobListStatic]
  );

  const extractCargoes = useCallback((jobDetails: Voyage) => {
    try {
      if (!jobDetails?.berths?.length) return [];

      const cargoes = jobDetails.berths
        .filter(
          (berth) =>
            berth?.calls?.some(
              (call) =>
                call.berth_id === jobDetails.currentBerth?.berth_id ||
                call.berth_id === jobDetails.nextBerth?.berth_id
            )
        )
        .flatMap((berth) =>
          (berth?.calls || [])
            .filter(
              (call) =>
                call.berth_id === jobDetails.currentBerth?.berth_id ||
                call.berth_id === jobDetails.nextBerth?.berth_id
            )
            .flatMap((call) =>
              (call?.operations || []).map((operation) => ({
                ...operation,
                berth:
                  jobDetails?.berthRotations.find(
                    (schedule) => schedule.berth_id === call.berth_id
                  )?.name || "",
                berthId: call.berth_id || "",
                jobId: jobDetails.id || "",
              }))
            )
        );

      return cargoes;
    } catch (error) {
      console.error("Error extracting cargoes:", error);
      return [];
    }
  }, []);

  const getVoyagesWithinTimeRange = useCallback(
    ({
      voyages,
      contactType,
      number,
      vesselId,
    }: {
      voyages: Voyage[];
      contactType:
        | "people"
        | "company"
        | "vessel"
        | "blacklist"
        | "phone"
        | null;
      number: string;
      vesselId?: string | null;
    }) => {
      try {
        const now = new Date();
        const past24 = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        const next48 = new Date(now.getTime() + 48 * 60 * 60 * 1000);

        const voyagesInRange = voyages.filter((voyage) => {
          // Check if voyage is associated with this contact
          const isVoyageMatch =
            contactType === "vessel"
              ? vesselId && voyage.vessel_id === vesselId
              : PhoneUtils.comparePhoneNumbers(
                  voyage.assigned_phone ?? "",
                  number
                ) ||
                PhoneUtils.comparePhoneNumbers(voyage.agentPhone ?? "", number);

          if (!isVoyageMatch || !voyage.berths?.length) return false;

          if (contactType === "vessel") return true;

          // Look inside each berth’s calls array
          const hasRelevantCall = voyage.berths.some((berth) =>
            berth.calls?.some((call) => {
              const eta = call.eta ? new Date(call.eta) : null;
              const etd = call.etd ? new Date(call.etd) : null;

              return (
                (eta && eta >= past24 && eta <= next48) ||
                (etd && etd >= past24 && etd <= next48)
              );
            })
          );

          return hasRelevantCall;
        });
        return voyagesInRange.map((voyage) => {
          const cargoes = extractCargoes(voyage);
          return {
            id: voyage.id,
            voyage: voyage.voyageNum,
            vesselName: voyage.name,
            // vesselId: voyage.,
            status: voyage.status,
            currentBerth: voyage.currentBerth?.name || null,
            nextBerth: voyage.nextBerth?.name || null,
            agent: voyage.agentName,
            management: voyage.management?.management_name || null,
            operator: voyage.operator || null,
            cargo: cargoes,
            assignedAgentId: voyage?.assignedAgentId || null,
          };
        });
      } catch (error) {
        console.error("Error getting voyages within time range:", error);
        return [];
      }
    },
    [extractCargoes]
  );

  const matchesAnyPhoneField = useCallback(
    (
      contact: any,
      phoneNumber: string,
      phoneFields: string[] = ["phone", "primary_phone", 'primaryPhone', "secondary_phone", "secondaryPhone"]
    ): boolean => {
      return phoneFields.some(
        (field) =>
          contact?.[field] &&
          PhoneUtils.comparePhoneNumbers(contact[field], phoneNumber)
      );
    },
    []
  );

  const findContact = useCallback(
    (number: string): ContactSearchResult => {
      try {
        // Search in blacklist first (highest priority)
        if (callsBlackList?.length) {
          const blacklistItem = callsBlackList.find(
            (blacklistData: any) =>
              blacklistData?.phone_number &&
              PhoneUtils.comparePhoneNumbers(blacklistData.phone_number, number)
          );
          if (blacklistItem) {
            return { contactDetails: blacklistItem, contactType: "blacklist" };
          }
        }

        // Search in vessel list - check phone, primary_phone, secondary_phone
        if (vesselList?.length) {
          const vessel = vesselList.find((vesselData) =>
            matchesAnyPhoneField(vesselData, number)
          );
          if (vessel) {
            return { contactDetails: vessel, contactType: "vessel" };
          }
        }

        // Search in people list - check phone, primary_phone, secondary_phone
        if (peopleList?.length) {
          const person = peopleList.find((personData) =>
            matchesAnyPhoneField(personData, number)
          );
          if (person) {
            return { contactDetails: person, contactType: "people" };
          }
        }

        // Search in company list
        if (companyList?.length) {
          const company = companyList.find((companyData) =>
            matchesAnyPhoneField(companyData, number)
          );
          if (company) {
            return { contactDetails: company, contactType: "company" };
          }
        }

        // Search in phone list (uses 'number' field)
        if (phoneList?.length) {
          const phone = phoneList.find(
            (phoneData) =>
              phoneData?.number &&
              PhoneUtils.comparePhoneNumbers(phoneData.number, number)
          );
          if (phone) {
            return { contactDetails: phone, contactType: "phone" };
          }
        }

        return { contactDetails: null, contactType: null };
      } catch (error) {
        console.error("Error finding contact:", error);
        return { contactDetails: null, contactType: null };
      }
    },
    [callsBlackList, vesselList, peopleList, companyList, phoneList, matchesAnyPhoneField]
  );

  const companyListById = useMemo(() => {
    const map: { [key: string]: CompanyList } = {};
    companyList.forEach((company) => {
      if (company.companyId) {
        map[company.companyId] = company;
      }
    });
    return map;
  }, [companyList]);

  const findJobDetails = useCallback(
    ({
      contactType,
      number,
      vesselId,
    }: {
      contactType:
        | "people"
        | "company"
        | "vessel"
        | "blacklist"
        | "phone"
        | null;
      number: string;
      vesselId?: string | null;
    }) => {
      try {
        if (!voyageList?.length) return null;
        if (contactType === "vessel") {
          return (
            voyageList.find((voyage) => voyage.vessel_id === vesselId) || null
          );
        } else {
          return (
            voyageList.find(
              (voyage) =>
                (voyage?.assigned_phone &&
                  PhoneUtils.comparePhoneNumbers(
                    voyage.assigned_phone,
                    number
                  )) ||
                (voyage?.agentPhone &&
                  PhoneUtils.comparePhoneNumbers(voyage.agentPhone, number))
            ) || null
          );
        }
      } catch (error) {
        console.error("Error finding job details:", error);
        return null;
      }
    },
    [voyageList]
  );

  const sortByBerthAndDate = useCallback(
    (items: any[], dateField: string) =>
      items.sort((a, b) => {
        // Sort by berth name first
        const berthComparison = (a?.berth || "").localeCompare(b?.berth || "");
        if (berthComparison !== 0) return berthComparison;

        // Then sort by date
        const aTime = a?.[dateField] ? new Date(a[dateField]).getTime() : 0;
        const bTime = b?.[dateField] ? new Date(b[dateField]).getTime() : 0;
        return aTime - bTime;
      }),
    []
  );

  const extractAndSortDeliveries = useCallback(
    (jobDetails: Voyage) => {
      try {
        if (!jobDetails?.berths?.length) return [];

        const deliveries = jobDetails.berths.flatMap((berth) =>
          (berth?.calls || [])
            .filter(
              (call) =>
                call.berth_id === jobDetails.currentBerth?.berth_id ||
                call.berth_id === jobDetails.nextBerth?.berth_id
            )
            .flatMap((call) =>
              (call?.deliveries || []).map((delivery) => ({
                ...delivery,
                berth: delivery?.dock_location || "",
              }))
            )
        );

        return sortByBerthAndDate(deliveries, "delivery_date");
      } catch (error) {
        console.error("Error extracting deliveries:", error);
        return [];
      }
    },
    [sortByBerthAndDate]
  );

  const extractAndSortBunkers = useCallback(
    (jobDetails: Voyage) => {
      try {
        if (!jobDetails?.berths?.length) return [];
        const bunkers = jobDetails.berths.flatMap((berth: any) =>
          (berth?.calls || [])
            .filter(
              (call: any) =>
                call.berth_id === jobDetails.currentBerth?.berth_id ||
                call.berth_id === jobDetails.nextBerth?.berth_id
            )
            .flatMap((call: any) =>
              (call?.bunkers || []).map((bunker: any) => ({
                ...bunker,
                berth: bunker?.dock_location || "",
              }))
            )
        );

        return sortByBerthAndDate(bunkers, "delivery_date");
      } catch (error) {
        console.error("Error extracting bunkers:", error);
        return [];
      }
    },
    [sortByBerthAndDate]
  );

  const extractAndSortOnboardServices = useCallback(
    (jobDetails: Voyage) => {
      try {
        if (!jobDetails?.berths?.length) return [];

        const services = jobDetails.berths.flatMap((berth) =>
          (berth?.calls || [])
            .filter(
              (call) =>
                call.berth_id === jobDetails.currentBerth?.berth_id ||
                call.berth_id === jobDetails.nextBerth?.berth_id
            )
            .flatMap((call) =>
              (call?.on_board_services || []).map((service) => ({
                ...service,
                berth: service?.dock_location || "",
              }))
            )
        );

        return sortByBerthAndDate(services, "date_completed");
      } catch (error) {
        console.error("Error extracting onboard services:", error);
        return [];
      }
    },
    [sortByBerthAndDate]
  );

  const extractOperations = useCallback((jobDetails: Voyage) => {
    try {
      if (!jobDetails?.berths?.length) return [];
      const operations = jobDetails.berths.flatMap((berth) =>
        (berth?.calls || []).flatMap((call) =>
          (call?.operations || []).map((operation) => ({
            ...operation,
            // berth: operation?.dock_location || "",
          }))
        )
      );

      return operations;
    } catch (error) {
      console.error("Error extracting operations:", error);
      return [];
    }
  }, []);

  const processChartererInsights = useCallback(
    (jobDetails: Voyage) => {
      try {
        const operationsData = extractOperations(jobDetails);

        const companyIdList =
          operationsData.map((operation) => operation.charterer_id) || [];

        if (jobDetails.company_id) companyIdList.push(jobDetails?.company_id);

        const uniqueCompanyIds = Array.from(new Set(companyIdList)).filter(
          (id) => id // Filter out undefined or null IDs
        );
        const chartererDetails = uniqueCompanyIds
          .filter((id) => companyListById[id]?.type?.value === "Charterer")
          .map(
            (id) =>
              companyListById[id] || {
                companyId: id,
                name: "Unknown Charterer",
              }
          );
        return chartererDetails;
      } catch (error) {
        console.error("Error processing charterer insights:", error);
        return [];
      }
    },
    [extractOperations, companyListById]
  );

  const collectNotificationListForVoyages = useCallback(
    (voyages: Voyage[]): any => {
      try {
        if (!voyages?.length || !notificationList?.length) return null;

        // Group notifications by voyage
        const voyageWiseNotifications: {
          [key: string]: {
            voyageId: string;
            voyageName: string;
            vesselName: string;
            persons: any[];
          };
        } = {};

        voyages.forEach((voyage) => {
          if (voyage.persons?.length) {
            voyageWiseNotifications[voyage.id] = {
              voyageId: voyage.id,
              voyageName: voyage.voyageNum || "Unknown Voyage",
              vesselName: voyage.name || "Unknown Vessel",
              persons: voyage.persons,
            };
          }
        });

        const voyageGroupArray = Object.values(voyageWiseNotifications);

        // If only one voyage with notifications, return flat persons array (as before)
        if (voyageGroupArray.length === 1) {
          return voyageGroupArray[0].persons;
        }

        // If multiple voyages with notifications, return voyage-wise structure for accordion
        if (voyageGroupArray.length > 1) {
          return voyageGroupArray;
        }

        // No notifications found
        return null;
      } catch (error) {
        console.error("Error collecting notification list for voyages:", error);
        return null;
      }
    },
    [notificationList]
  );

  const filterVoyagesByOwnerCompanyId = useCallback(
    (companyId: string): Voyage[] => {
      try {
        if (!voyageList?.length || !companyId) return [];

        const voyageIds = new Set<string>();
        const filteredVoyages: Voyage[] = [];

        voyageList.forEach((voyage) => {
          if (voyageIds.has(voyage.id)) return;

          let isMatch = false;

          const matchReasons: string[] = [];

          if (voyage.berthRotations?.length) {
            const hasBerthRotationWithOwner = voyage.berthRotations.some((rotation: any) => {
              return rotation?.owner_company_id && String(rotation.owner_company_id) === String(companyId);
            });
            if (hasBerthRotationWithOwner) {
              isMatch = true;
              matchReasons.push("owner_company_id");
            }
          }

          if (voyage.berths?.length) {
            const hasMatchingRelation = voyage.berths.some((berth: any) => {
              if (!berth.calls?.length) return false;

              return berth.calls.some((call: any) => {
                if (call.deliveries?.length) {
                  const matchesDeliveryVendor = call.deliveries.some((delivery: any) =>
                    delivery?.vendor_id && String(delivery.vendor_id) === String(companyId)
                  );
                  if (matchesDeliveryVendor) {
                    return true;
                  }
                }

                // Check bunkers supplier_id
                if (call.bunkers?.length) {
                  const matchesBunkerSupplier = call.bunkers.some((bunker: any) =>
                    bunker?.supplier_id && String(bunker.supplier_id) === String(companyId)
                  );
                  if (matchesBunkerSupplier) {
                    return true;
                  }
                }

                if (call.on_board_services?.length) {
                  const matchesServiceVendor = call.on_board_services.some((service: any) =>
                    service?.vendor_id && String(service.vendor_id) === String(companyId)
                  );
                  if (matchesServiceVendor) {
                    return true;
                  }
                }

                if (call.operations?.length) {
                  const matchesOperation = call.operations.some((operation: any) =>
                    (operation?.charterer_id && String(operation.charterer_id) === String(companyId)) ||
                    (operation?.surveyor_id && String(operation.surveyor_id) === String(companyId))
                  );
                  if (matchesOperation) {
                    return true;
                  }
                }

                return false;
              });
            });

            if (hasMatchingRelation) {
              isMatch = true;
              if (!matchReasons.includes("owner_company_id")) {
                matchReasons.push("vendor/supplier/charterer/surveyor");
              }
            }
          }

          if (isMatch) {
            voyageIds.add(voyage.id);
            filteredVoyages.push(voyage);
          }
        });

        return filteredVoyages;
      } catch (error) {
        console.error("Error filtering voyages by owner company ID:", error);
        return [];
      }
    },
    [voyageList]
  );

  const filterArrangementsByCompanyId = useCallback(
    (
      voyages: Voyage[],
      companyId: string
    ): VesselArrangements => {
      try {
        if (!voyages?.length || !companyId) {
          return { deliveries: [], bunkers: [], onboardServices: [] };
        }

        const allDeliveries: any[] = [];
        const allBunkers: any[] = [];
        const allOnboardServices: any[] = [];

        voyages.forEach((voyage) => {
          if (!voyage.berths?.length) return;

          voyage.berths.forEach((berth) => {
            if (!berth.calls?.length) return;

            berth.calls.forEach((call) => {
              if (call.deliveries?.length) {
                const matchingDeliveries = call.deliveries.filter(
                  (delivery: any) =>
                    delivery?.vendor_id &&
                    String(delivery.vendor_id) === String(companyId)
                );
                allDeliveries.push(
                  ...matchingDeliveries.map((d: any) => ({
                    ...d,
                    berth: d.dock_location || "",
                  }))
                );
              }

              // Filter bunkers by supplier_id
              if (call.bunkers?.length) {
                const matchingBunkers = call.bunkers.filter(
                  (bunker: any) =>
                    bunker?.supplier_id &&
                    String(bunker.supplier_id) === String(companyId)
                );
                allBunkers.push(
                  ...matchingBunkers.map((b: any) => ({
                    ...b,
                    berth: b.dock_location || "",
                  }))
                );
              }

              // Filter onboard services (freshwater, laundry, etc.) by vendor_id
              if (call.on_board_services?.length) {
                const matchingServices = call.on_board_services.filter(
                  (service: any) =>
                    service?.vendor_id &&
                    String(service.vendor_id) === String(companyId)
                );
                
                allOnboardServices.push(
                  ...matchingServices.map((s: any) => ({
                    ...s,
                    berth: s.dock_location || "",
                  }))
                );
              }
            });
          });
        });

        return {
          deliveries: sortByBerthAndDate(allDeliveries, "delivery_date"),
          bunkers: sortByBerthAndDate(allBunkers, "delivery_date"),
          onboardServices: sortByBerthAndDate(
            allOnboardServices,
            "date_completed"
          ),
        };
      } catch (error) {
        console.error("Error filtering arrangements by company ID:", error);
        return { deliveries: [], bunkers: [], onboardServices: [] };
      }
    },
    [sortByBerthAndDate]
  );

  const processVesselArrangements = useCallback(
    (jobDetails: Voyage): VesselArrangements => {
      try {
        const deliveriesData = extractAndSortDeliveries(jobDetails);
        const bunkersData = extractAndSortBunkers(jobDetails);
        const onboardData = extractAndSortOnboardServices(jobDetails);
        return {
          deliveries: deliveriesData,
          bunkers: bunkersData,
          onboardServices: onboardData,
        };
      } catch (error) {
        console.error("Error processing vessel arrangements:", error);
        return { deliveries: [], bunkers: [], onboardServices: [] };
      }
    },
    [
      extractAndSortBunkers,
      extractAndSortDeliveries,
      extractAndSortOnboardServices,
    ]
  );

  const createCallData = useCallback(
    (
      id: string,
      time: string,
      contactDetails: People | CompanyList | any | null,
      contactType:
        | "people"
        | "company"
        | "vessel"
        | "blacklist"
        | "phone"
        | null,
      jobDetails: any,
      arrangements: VesselArrangements | null,
      number: string,
      voyages: any[],
      chartererInsights: CompanyList[],
      status: string,
      vessels?: any[],
      notificationListForVoyages?: any[]
    ) => {
      try {
        const getName = () => {
          if (contactType === "vessel" && contactDetails) {
            return contactDetails.name || "Unknown Vessel";
          }
          if (contactType === "blacklist") {
            return "Blacklist";
          }
          if (contactType === "phone" && contactDetails) {
            return contactDetails.name || "Phone Contact";
          }
          return contactDetails?.name || "Unknown";
        };

        const getPhone = () => {
          if (contactType === "blacklist" && contactDetails) {
            return contactDetails.phone_number || number;
          }
          if (contactType === "phone" && contactDetails) {
            return contactDetails.number || number;
          }
          // // For vessel/people/company, return the specific field that matched the incoming number
          if (contactDetails) {
            const phoneFields = ["phone", "primary_phone", "primaryPhone", "secondary_phone", "secondaryPhone"];
            const matchedField = phoneFields.find(
              (field) =>
                contactDetails[field] &&
                PhoneUtils.comparePhoneNumbers(contactDetails[field], number)
            );
            if (matchedField) return contactDetails[matchedField];
          }
          return number;
        };

        return {
          id,
          name: getName(),
          phone: getPhone(),
          contactType,
          jobId: jobDetails?.id || null,
          person:
            contactType === "people"
              ? {
                  personName: contactDetails?.name || "Unknown",
                  personId:
                    contactType === "people" && contactDetails
                      ? (contactDetails as People).personId || "Unknown"
                      : "Unknown",
                  title:
                    contactType === "people" && contactDetails
                      ? (contactDetails as People).title || "N/A"
                      : "N/A",
                  position:
                    contactDetails && "position" in contactDetails
                      ? contactDetails.position?.value || "N/A"
                      : "N/A",
                  email: contactDetails?.email || "N/A",
                  company:
                    contactType === "people" &&
                    contactDetails &&
                    "company" in contactDetails
                      ? contactDetails.company?.name || "N/A"
                      : "N/A",
                }
              : null,

          company:
            contactType === "company"
              ? {
                  name: contactDetails?.name || "Unknown",
                  companyId:
                    contactType === "company" && contactDetails
                      ? (contactDetails as CompanyList).companyId || "Unknown"
                      : "Unknown",
                  type:
                    contactType === "company" &&
                    contactDetails &&
                    "type" in contactDetails &&
                    contactDetails.type
                      ? contactDetails.type.value
                      : "N/A",
                }
              : null,
          vessel: jobDetails
            ? {
                vesselName: jobDetails?.name || "Unknown Vessel",
                voyage: jobDetails?.voyageNum || "Unknown Voyage",
                operator: jobDetails?.operator || "Unknown Operator",
                managementCompany:
                  jobDetails?.management?.management_name ||
                  "Unknown Management",
                agent: jobDetails?.agentName || undefined,
                appointedOperator: jobDetails.appointedOperator,
                appointedManagement: jobDetails.appointedManagement,
                currentStatus: jobDetails?.status || "N/A",
                currentBerth: jobDetails?.currentBerth?.name || null,
                currentBerthId: jobDetails?.currentBerth?.berth_id || null,
                eta: jobDetails?.currentBerth?.eta || null,
                etd: jobDetails?.currentBerth?.etd || null,
                nextBerth: jobDetails?.nextBerth?.name || null,
                assignedAgentId: jobDetails?.assigned_agent_id || null,
              }
            : null,
          vessels: vessels,
          arrangements: arrangements || {
            deliveries: [],
            bunkers: [],
            onboardServices: [],
          },
          voyages,
          chartererInsights,
          notificationList: notificationListForVoyages || jobDetails?.persons,
          active:
            status === "ringing" || status === "in-progress" ? true : false,
          time,
        };
        // }

        // return null;
      } catch (error) {
        console.error("Error creating call data:", error);
        return null;
      }
    },
    []
  );

  const handleIncomingCalls = useCallback(
    ({
      from,
      // to,
      time,
      id,
      status,
    }: {
      from: string;
      to?: string;
      time: string;
      id: string;
      status: string;
    }): CallData | null => {
      try {
        // Validate input
        if (!from?.trim()) {
          console.warn("Phone number is required");
          return null;
        }

        // Search for contact details
        const { contactDetails, contactType } = findContact(from);
        
        if (contactType === "company" && contactDetails?.companyId) {
          const companyId = contactDetails.companyId;
          // CASE 1: Filter voyages by berth rotation owner company id
          const matchingVoyages = filterVoyagesByOwnerCompanyId(companyId);

          // CASE 2: Filter arrangements from these voyages by company id
          const filteredArrangements = filterArrangementsByCompanyId(
            matchingVoyages,
            companyId
          );

          // Build vessels array from matching voyages
          const vessels = matchingVoyages.map((voyage) => ({
            vesselName: voyage.name || "Unknown Vessel",
            voyage: voyage.voyageNum || "Unknown Voyage",
            operator: voyage.operator || "Unknown Operator",
            managementCompany:
              voyage.management?.management_name || "Unknown Management",
            agent: voyage.agentName || undefined,
            currentStatus: voyage.status || "N/A",
            currentBerth: voyage.currentBerth?.name || null,
            currentBerthId: voyage.currentBerth?.berth_id || null,
            eta: voyage.currentBerth?.eta || null,
            etd: voyage.currentBerth?.etd || null,
            nextBerth: voyage.nextBerth?.name || null,
            appointedOperator: voyage.appointedOperator || false,
            appointedManagement: voyage.appointedManagement || false,
            assignedAgentId: voyage.assignedAgentId || null,
          }));

          // Extract charterer insights from matching voyages
          const uniqueCharterers = new Map<string, CompanyList>();
          matchingVoyages.forEach((voyage) => {
            const charterers = processChartererInsights(voyage);
            charterers.forEach((charterer) => {
              if (charterer.companyId && !uniqueCharterers.has(charterer.companyId)) {
                uniqueCharterers.set(charterer.companyId, charterer);
              }
            });
          });

          // Convert voyages to voyagesList format for display
          const voyagesList = matchingVoyages.map((voyage) => {
            const cargoes = extractCargoes(voyage);
            return {
              id: voyage.id,
              voyage: voyage.voyageNum || "Unknown Voyage",
              vesselName: voyage.name || "Unknown Vessel",
              vesselId: voyage.vessel_id || voyage.id,
              status: voyage.status || "N/A",
              currentBerth: voyage.currentBerth?.name || null,
              nextBerth: voyage.nextBerth?.name || null,
              agent: voyage.agentName || null,
              management: voyage.management?.management_name || null,
              operator: voyage.operator || "Unknown Operator",
              cargo: cargoes, // Extract cargo from all voyage operations
            };
          });

          // Create call data for company with multiple vessels
          const companyCallData = createCallData(
            id,
            time,
            contactDetails,
            contactType,
            null, // No single jobDetails for multi-voyage company calls
            filteredArrangements,
            from,
            voyagesList, // Pass voyages list for display
            Array.from(uniqueCharterers.values()), // Pass extracted charterer insights
            status,
            vessels, // Pass vessels array for accordion display
            collectNotificationListForVoyages(matchingVoyages)
          );

          return companyCallData;
        } else if (contactType === "people" && contactDetails?.companyId) {

        const companyId = contactDetails.companyId;

          // CASE 1: Filter voyages by berth rotation owner company id
          const matchingVoyages = filterVoyagesByOwnerCompanyId(companyId);

          // CASE 2: Filter arrangements from these voyages by company id
          const filteredArrangements = filterArrangementsByCompanyId(
            matchingVoyages,
            companyId
          );

          // Build vessels array from matching voyages
          const vessels = matchingVoyages.map((voyage) => ({
            vesselName: voyage.name || "Unknown Vessel",
            voyage: voyage.voyageNum || "Unknown Voyage",
            operator: voyage.operator || "Unknown Operator",
            managementCompany:
              voyage.management?.management_name || "Unknown Management",
            agent: voyage.agentName || undefined,
            currentStatus: voyage.status || "N/A",
            currentBerth: voyage.currentBerth?.name || null,
            currentBerthId: voyage.currentBerth?.berth_id || null,
            eta: voyage.currentBerth?.eta || null,
            etd: voyage.currentBerth?.etd || null,
            nextBerth: voyage.nextBerth?.name || null,
            appointedOperator: voyage.appointedOperator || false,
            appointedManagement: voyage.appointedManagement || false,
            assignedAgentId: voyage.assignedAgentId || null,
          }));

          // Extract charterer insights from matching voyages
          const uniqueCharterers = new Map<string, CompanyList>();
          matchingVoyages.forEach((voyage) => {
            const charterers = processChartererInsights(voyage);
            charterers.forEach((charterer) => {
              if (charterer.companyId && !uniqueCharterers.has(charterer.companyId)) {
                uniqueCharterers.set(charterer.companyId, charterer);
              }
            });
          });

          // Convert voyages to voyagesList format for display
          const voyagesList = matchingVoyages.map((voyage) => {
            const cargoes = extractCargoes(voyage);
            return {
              id: voyage.id,
              voyage: voyage.voyageNum || "Unknown Voyage",
              vesselName: voyage.name || "Unknown Vessel",
              vesselId: voyage.vessel_id || voyage.id,
              status: voyage.status || "N/A",
              currentBerth: voyage.currentBerth?.name || null,
              nextBerth: voyage.nextBerth?.name || null,
              agent: voyage.agentName || null,
              management: voyage.management?.management_name || null,
              operator: voyage.operator || "Unknown Operator",
              cargo: cargoes, // Extract cargo from all voyage operations
            };
          });

          // Create call data for company with multiple vessels
          const companyCallData = createCallData(
            id,
            time,
            contactDetails,
            contactType,
            null, // No single jobDetails for multi-voyage company calls
            filteredArrangements,
            from,
            voyagesList, // Pass voyages list for display
            Array.from(uniqueCharterers.values()), // Pass extracted charterer insights
            status,
            vessels, // Pass vessels array for accordion display
            collectNotificationListForVoyages(matchingVoyages)
          );

          return companyCallData;
        }

        // --- Standard flow for other contact types ---
        const jobDetails = findJobDetails({
          contactType,
          number: from,
          vesselId: contactType === "vessel" ? contactDetails?.vessel_id : null,
        });

        // Process vessel arrangements if job details exist
        const arrangements = jobDetails
          ? processVesselArrangements(jobDetails)
          : null;

        const voyages = getVoyagesWithinTimeRange({
          voyages: voyageList,
          contactType,
          number: from,
          vesselId: contactType === "vessel" ? contactDetails?.vessel_id : null,
        });

        const chartererInsights = jobDetails
          ? processChartererInsights(jobDetails)
          : [];

        // Create call data if valid contact
        const callCardData = createCallData(
          id,
          time,
          contactDetails,
          contactType,
          jobDetails,
          arrangements,
          from,
          voyages,
          chartererInsights,
          status
        );

        return callCardData;

        // Close the phone test dialog
      } catch (error) {
        console.error("Error in handlePlayPhone:", error);
        // Ensure dialog closes even on error
        return null;
      }
    },
    [
      createCallData,
      findContact,
      findJobDetails,
      processVesselArrangements,
      getVoyagesWithinTimeRange,
      voyageList,
      processChartererInsights,
      filterVoyagesByOwnerCompanyId,
      filterArrangementsByCompanyId,
      collectNotificationListForVoyages,
      extractCargoes
    ]
  );

  const filterOldCalls = (calls: CallData[]) => {
    if (!calls) return [];

    return calls.filter((call) => {
      if (!call.time) return true; // Keep calls without time data

      const callTime = dayjs(call.time);
      const now = dayjs();
      const minutesDiff = now.diff(callTime, "minute");

      return minutesDiff <= 10; // Only keep calls within 10 minutes
    });
  };

  const callData = useMemo(
    () =>
      incomingCallsList
        ?.map((call) => handleIncomingCalls(call))
        .filter((call) => call !== null),
    [incomingCallsList, handleIncomingCalls]
  );

  useEffect(() => {
    // Filter calls when callData changes
    setFilteredCallData(filterOldCalls(callData ?? []));
  }, [callData]);
  return (
    <Box minWidth={300}>
      {peopleLoading || companyLoading || voyageLoading ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: 200,
          }}
        >
          <Typography variant="h6" gutterBottom>
            <CircularProgress />
          </Typography>
        </Box>
      ) : (
        <>
          {filteredCallData?.map((call) => (
            <CallCard key={call.id} data={call} currentTime={currentTime} />
          ))}
          {!filteredCallData ||
            (filteredCallData?.length === 0 && (
              <Box pt={5}>
                <Box
                  bgcolor="grey.100"
                  width="80px"
                  height="80px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="15px"
                  margin="auto"
                >
                  <CallIcon
                    fontSize="large"
                    sx={{
                      color: "grey.500",
                    }}
                  />
                </Box>
                <Typography variant="h6" align="center" sx={{ mt: 2 }}>
                  No Incoming Calls
                </Typography>
              </Box>
            ))}
        </>
      )}
    </Box>
  );
};

export default CallSidebar;
